import React, { useState } from "react";
import axios from "axios";
import { Loader2, Building2, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LenderLoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login: authLogin } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email, password, login_context: "lender_portal",
      }, { withCredentials: true });
      // Update AuthContext state (not just localStorage)
      localStorage.setItem("ic_token", res.data.token);
      localStorage.setItem("ic_user", JSON.stringify(res.data.user));
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
      onLogin(res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      if (detail.includes("not yet approved")) {
        setError("Your lender account is pending approval. Please use the main sign-in page for conditional access.");
      } else {
        setError(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] to-[#1a0a2e] flex items-center justify-center px-4" data-testid="lender-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-purple-900/50 border border-purple-700/50 flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-7 w-7 text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Lender Portal</h1>
          <p className="text-sm text-purple-400/70 mt-1">Imaani Capital Lending</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#1a0a2e] rounded-2xl border border-purple-800/30 p-8 space-y-5">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-900/30 border border-red-700/50 text-red-300 text-xs" data-testid="lender-login-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-purple-300/80 mb-1.5">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
              data-testid="lender-login-email"
              className="w-full px-4 py-3 rounded-xl bg-[#0A0A0A] border border-purple-700/30 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-purple-500"
              placeholder="lender@example.com" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-purple-300/80 mb-1.5">Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
              data-testid="lender-login-password"
              className="w-full px-4 py-3 rounded-xl bg-[#0A0A0A] border border-purple-700/30 text-white text-sm placeholder:text-gray-600 focus:outline-none focus:border-purple-500"
              placeholder="Enter your password" />
          </div>

          <button type="submit" disabled={loading} data-testid="lender-login-submit"
            className="w-full py-3 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Access Lender Portal
          </button>

          <p className="text-center text-[10px] text-gray-500 mt-4">
            This portal is for approved lenders only. <br/>
            New lenders can register at the main sign-in page.
          </p>
        </form>
      </div>
    </div>
  );
}
